/**
 * This component displays a modal popup for managing duplicate materials 
 * detected in the inventory. It allows users to:
 * - View potential duplicate materials based on matching item name or part number.
 * - Set visibility flags (`hide from search`) for each duplicate.
 * - Change the catalog type (Internal or External) of a duplicate.
 * - Select the appropriate catalog/category based on catalog type.
 * 
 * FEATURES:
 * - Filters duplicate materials excluding the current material by comparing
 *   lowercase `item` and `partNo` fields.
 * - Uses `CustomTable` to list duplicates with editable fields.
 * - Dispatches `setBulkMaterialDuplicateVisibility` to persist bulk updates.
 * 
 * PROPS:
 * - material: Object → The main material under review for duplication.
 * - allMaterials: Array → Full list of materials used for duplicate detection.
 * - onClose: Function → Callback to close the modal.
 * - onUpdateDuplicates: Function → Callback triggered after updating duplicates.
 * - onRefreshDuplicates: Function → Callback to refresh the duplicate list with current search/filter settings.
 * - onRefreshMaterialList: Function → Callback to refresh the material list page.
 * - onRefreshItemTemplateList: Function → Callback to refresh the item template list page.
 * - catalogType: String → Default catalog type (`"internal"` or `"external"`).
 * - categoryList: Array → List of catalog categories used for selection.
 * 
 * USAGE:
 * Rendered as a modal from a parent component to help manage and resolve 
 * potential duplicate materials in the system.
 */
import React, { useState, useEffect } from 'react';
import { useDispatch } from "react-redux";
import Modal from '../../../components/modal/modal';
import CustomTable from '../../../components/table/table';
import SelectInput from '../../../components/inputs/select';
import { setBulkMaterialDuplicateVisibility, countAffectedTemplates, replaceAndHideMaterials } from '../../../actions/inventory.action';
import { errorSnackBar, successSnackBar } from '../../../actions/common.action';
import { toUSD } from 'src/util/currency-formatter';
import { Checkbox, Radio } from '@mui/material';

export default function DuplicateMaterialPopup({
    material,
    onClose,
    onUpdateDuplicates,
    onRefreshDuplicates, // New prop for refreshing duplicate list
    onRefreshMaterialList, // New prop for refreshing material list
    onRefreshItemTemplateList, // New prop for refreshing item template list
    categoryList = [],
    writePermission = false
}) {
    const [duplicates, setDuplicates] = useState([]);
    const [selectedReplaceAndHideItems, setSelectedReplaceAndHideItems] = useState([]);
    const [selectedReplacement, setSelectedReplacement] = useState(null);
    const [showReplaceHideConfirmation, setShowReplaceHideConfirmation] = useState(false);
    const [affectedCount, setAffectedCount] = useState(0);
    const [affectedTemplateIds, setAffectedTemplateIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (!material) return;
        const isHidden = (o) => Boolean(o?.hideSearch ?? o?.hide_search ?? o?.hide ?? false);

        const currentHideStatus = isHidden(material);

        const selected = {
            ...material,
            cost: (material.costValue) || (material.cost) || 0,
            catalogId: material.category?.vendor_id ? '2' : '1',
            categoryId: material.categoryId || '',
            hide: currentHideStatus,
            originalHide: currentHideStatus,
            originalCategoryId: material.categoryId || '',
        };

        const matches = (material.duplicates || []).map(m => ({
            ...m,
            cost: (m.costValue) || (m.cost) || 0,
            catalogId: m.category?.vendor_id ? '2' : '1',
            categoryId: m.categoryId || '',
            hide: isHidden(m),
            originalHide: isHidden(m),
            originalCategoryId: m.categoryId || '',
        }));

        const combined = [selected, ...matches];
        setDuplicates(combined);
    }, [material]);

    if (!material) {
        return null;
    }

    const handleSave = async () => {
        if (selectedReplaceAndHideItems.length > 0 && !selectedReplacement) {
            dispatch(errorSnackBar("Please select a replacement material for the items to be replaced and hidden."));
            return;
        }

        if (selectedReplaceAndHideItems.length > 0 && selectedReplacement) {
            try {
                setIsLoading(true);
                const result = await dispatch(countAffectedTemplates({
                    replaceItems: selectedReplaceAndHideItems,
                    replacementItem: selectedReplacement
                }));

                if (result && result.count !== undefined) {
                    setAffectedCount(result.count);
                    setAffectedTemplateIds(result.templateIds || []);
                    setShowReplaceHideConfirmation(true);
                } else {
                    dispatch(errorSnackBar("Failed to count affected templates."));
                }
            } catch (e) {
                dispatch(errorSnackBar("Failed to count affected templates. Please try again later."));
            } finally {
                setIsLoading(false);
            }
            return;
        }

        const updates = duplicates
            .filter(d => (d.categoryId || '') !== (d.originalCategoryId || ''))
            .map(d => ({
                id: d.id,
                categoryId: d.categoryId
            }));

        if (!updates.length) {
            onClose();
            return;
        }

        try {
            await dispatch(setBulkMaterialDuplicateVisibility(updates));

            dispatch(successSnackBar(`${updates.length} material(s) updated successfully`));

            onClose();

            if (onRefreshDuplicates) {
                onRefreshDuplicates();
            }
            
            if (onRefreshItemTemplateList && typeof onRefreshItemTemplateList === 'function') {
                onRefreshItemTemplateList();
            }
        } catch (e) {
            dispatch(errorSnackBar("Failed to update materials. Please try again later."));
        }
    };

    const handleConfirmReplaceAndHide = async () => {
        setIsLoading(true);
        try {
            const bulkUpdates = duplicates
                .filter(d => !selectedReplaceAndHideItems.includes(d.id) &&
                    (d.categoryId || '') !== (d.originalCategoryId || ''))
                .map(d => ({
                    id: d.id,
                    categoryId: d.categoryId
                }));

            const result = await dispatch(replaceAndHideMaterials({
                replaceItems: selectedReplaceAndHideItems,
                replacementItem: selectedReplacement,
                templateIds: affectedTemplateIds,
                bulkUpdates: bulkUpdates.length > 0 ? bulkUpdates : undefined
            }));
            if (result && (result.updatedCount > 0 || result.hiddenMaterials > 0)) {
                let successMessage = '';
                if (result.updatedCount > 0 && result.hiddenMaterials > 0) {
                    successMessage = `${result.hiddenMaterials} material(s) replaced and hidden in ${result.updatedCount} item template(s)`;
                } else if (result.updatedCount > 0) {
                    successMessage = `${result.updatedCount} item template(s) updated`;
                } else if (result.hiddenMaterials > 0) {
                    successMessage = `${result.hiddenMaterials} material(s) hidden`;
                }

                dispatch(successSnackBar(successMessage));
                setSelectedReplaceAndHideItems([]);
                setSelectedReplacement(null);
                setShowReplaceHideConfirmation(false);

                onClose();

                if (onRefreshDuplicates) {
                    onRefreshDuplicates();
                }
                
                if (onRefreshItemTemplateList && typeof onRefreshItemTemplateList === 'function') {
                    onRefreshItemTemplateList();
                }
            } else {
                dispatch(errorSnackBar("Failed to replace and hide materials."));
            }
        } catch (e) {
            dispatch(errorSnackBar("Failed to replace and hide materials. Please try again later."));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelReplaceAndHide = () => {
        setShowReplaceHideConfirmation(false);
        setSelectedReplaceAndHideItems([]);
        setSelectedReplacement(null);
        setAffectedCount(0);
        setAffectedTemplateIds([]);
    };

    const handleReplaceAndHideToggle = (itemId) => {
        setSelectedReplaceAndHideItems(prev => {
            const isSelected = prev.includes(itemId);
            const next = isSelected ? prev.filter(id => id !== itemId) : [...prev, itemId];
            if (!isSelected && selectedReplacement === itemId) {
                setSelectedReplacement(null);
            }
            return next;
        });
    };

    const handleReplacementSelect = (itemId) => {
        if (selectedReplaceAndHideItems.includes(itemId)) {
            dispatch(errorSnackBar("Replacement material cannot be in the list of materials to be replaced and hidden."));
            return;
        }
        setSelectedReplacement(itemId);
    };

    const updateDuplicate = (id, changes) => {
        setDuplicates(prev =>
            prev.map(d => d.id === id ? { ...d, ...changes } : d)
        );
    };

    const hasPendingChanges = () => {
        if (selectedReplaceAndHideItems.length > 0 && selectedReplacement) {
            return true;
        }

        const hasRegularUpdates = duplicates.some(d =>
            (d.categoryId || '') !== (d.originalCategoryId || '')
        );

        return hasRegularUpdates;
    };

    const getCategoriesByType = (type) => {
        return categoryList.filter(cat =>
            type === '1' ? !cat.vendor_id : type === '2' ? !!cat.vendor_id : true
        ).map(cat => ({
            key: cat.name,
            value: cat.id
        }));
    };

    const columns = [
        {
            title: 'Replace & Hide',
            field: 'replaceAndHide',
            render: (rowData) => (
                <div className="d-flex justify-content-center align-items-center">
                    <Checkbox
                        data-duplicate-replace-hide={`duplicate-replace-hide-${rowData.id}`}
                        checked={selectedReplaceAndHideItems.includes(rowData.id)}
                        onChange={() => handleReplaceAndHideToggle(rowData.id)}
                        disabled={!writePermission}
                        size="small"
                    />
                </div>
            )
        },
        {
            title: 'Use As Replacement',
            field: 'replacement',
            render: (rowData) => (
                <div className="d-flex justify-content-center align-items-center">
                    <Radio
                        data-duplicate-replacement={`duplicate-replacement-${rowData.id}`}
                        checked={selectedReplacement === rowData.id}
                        onChange={() => handleReplacementSelect(rowData.id)}
                        disabled={!writePermission || selectedReplaceAndHideItems.includes(rowData.id)}
                        size="small"
                    />
                </div>
            )
        },
        {
            title: 'Item (Part No)',
            field: 'item',
            render: (rowData) => (
                <div className={`${rowData.hide ? 'text-muted' : ''}`}>
                    {rowData.item} ({rowData.partNo || 'No Part No'})
                    {rowData.hide && (
                        <span className="badge bg-secondary" title="This material is currently hidden from search">
                            Hidden
                        </span>
                    )}
                </div>
            )
        },
        {
            title: 'Cost',
            field: 'cost',
            render: (rowData) => rowData.cost ? `${toUSD(rowData.cost)}` : '$0.00',
        },
        {
            title: 'Type',
            field: 'catalogId',
            render: (rowData) => (
                <SelectInput
                    data-duplicate-catalogId={`duplicate-catalogId-${rowData.id}`}
                    value={rowData.catalogId}
                    onChange={(e) => updateDuplicate(rowData.id, {
                        catalogId: e.target.value,
                        categoryId: ''
                    })}
                    disabled={!writePermission}
                    items={[
                        { key: "Internal", value: "1" },
                        { key: "External", value: "2" }
                    ]}
                    placeholder="-- Select Type --"
                />
            )
        },
        {
            title: 'Catalog',
            field: 'categoryId',
            render: (rowData) => (
                <SelectInput
                    data-duplicate-categoryId={`duplicate-categoryId-${rowData.id}`}
                    value={rowData.categoryId}
                    onChange={(e) => updateDuplicate(rowData.id, { categoryId: e.target.value })}
                    items={getCategoriesByType(rowData.catalogId)}
                    disabled={!writePermission}
                    placeholder="-- Select Catalog --"
                />
            )
        }
    ];

    const numberedDuplicates = duplicates.map((dup, i) => ({ ...dup, serial: i + 1 }));

    return (
        <>
            <Modal
                open={true}
                onClose={onClose}
                modalHeading={`Manage Duplicates for "${material.item}"`}
                modalContent={
                    <div>
                        <div className="alert alert-info mb-3">
                            <div className="d-flex align-items-start">
                                <div className="me-2">ℹ️</div>
                                <div>
                                    <strong>New Feature:</strong> Use the "Replace & Hide" checkbox to simultaneously replace materials in templates and hide them from search.
                                    This simplifies purging duplicate materials from your catalogue.
                                    <br />
                                    <small className="text-muted">
                                        • Select materials to replace and hide using the checkbox
                                        • Choose a replacement material using the radio button
                                        • Click "Save Changes" to confirm the operation
                                        • Category changes can still be made separately
                                    </small>
                                </div>
                            </div>
                        </div>
                        <CustomTable
                            columns={columns}
                            data={numberedDuplicates}
                            toolbar={false}
                            exportButton={false}
                            addButton={false}
                            isEdit={false}
                            isDelete={false}
                            isAction={false}
                            paginationCount={numberedDuplicates.length}
                            enableSearch={false}
                            itemsPerPageCustom={false}
                            stickyHeader={false}
                        />

                    </div >
                }
                modalCloseTxt="CANCEL"
                modalSaveTxt="SAVE CHANGES"
                onClick={handleSave}
                modalSaveTxtDisabledClass={isLoading || !hasPendingChanges() ? 'disabled' : ''}
                showActionBtn={writePermission}
                fullWidth={true}
                maxWidth="lg"
            />

            {showReplaceHideConfirmation && (
                <Modal
                    open={showReplaceHideConfirmation}
                    onClose={handleCancelReplaceAndHide}
                    modalHeading="Confirm Replace & Hide"
                    modalContent={
                        <div className="p-3">
                            <div className="mb-3">
                                <h6>Items to be hidden and replaced:</h6>
                                <ul className="list-unstyled">
                                    {selectedReplaceAndHideItems.map(itemId => {
                                        const item = duplicates.find(d => d.id === itemId);
                                        return (
                                            <li key={itemId} className="mb-1">
                                                <span className="text-muted">• {item?.item} ({item?.partNo || 'No Part No'})</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                            <div className="mb-3">
                                <h6>Replacement material:</h6>
                                <p className="text-primary mb-0">
                                    <strong>{duplicates.find(d => d.id === selectedReplacement)?.item} ({duplicates.find(d => d.id === selectedReplacement)?.partNo || 'No Part No'})</strong>
                                </p>
                            </div>
                            <div className="mb-3">
                                <h6>No. of templates to be updated:</h6>
                                <p className="text-info mb-0">
                                    <strong>{affectedCount}</strong> template(s)
                                </p>
                            </div>
                            {(() => {
                                const bulkUpdates = duplicates
                                    .filter(d => !selectedReplaceAndHideItems.includes(d.id) &&
                                        (d.categoryId || '') !== (d.originalCategoryId || ''))
                                    .map(d => ({
                                        id: d.id,
                                        categoryId: d.categoryId,
                                        item: d.item,
                                        partNo: d.partNo,
                                        hide: d.hide,
                                        originalHide: d.originalHide,
                                        originalCategoryId: d.originalCategoryId
                                    }));

                                if (bulkUpdates.length > 0) {
                                    return (
                                        <div className="mb-3">
                                            <h6>Additional changes to be made:</h6>
                                            <ul className="list-unstyled">
                                                {bulkUpdates.map(item => {
                                                    const hasHideChange = item.hide !== item.originalHide;
                                                    const hasCategoryChange = (item.categoryId || '') !== (item.originalCategoryId || '');

                                                    return (
                                                        <li key={item.id} className="mb-1">
                                                            <span className="text-muted">• {item.item} ({item.partNo || 'No Part No'})</span>
                                                            {hasHideChange && (
                                                                <span className="ms-2 badge bg-warning">
                                                                    {item.hide ? 'Will be hidden' : 'Will be shown'}
                                                                </span>
                                                            )}
                                                            {hasCategoryChange && (
                                                                <span className="ms-2 badge bg-info">
                                                                    Category changed
                                                                </span>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            {affectedCount === 0 ? (
                                <div className="alert alert-info">
                                    <small>
                                        <strong>Note:</strong> No item templates contain the selected materials to replace.
                                        The materials will still be hidden from search.
                                    </small>
                                </div>
                            ) : (
                                <div className="alert alert-warning">
                                    <small>
                                        <strong>Note:</strong> This action will replace the selected materials in all affected templates
                                        and hide them from search. This operation cannot be undone.
                                    </small>
                                </div>
                            )}
                        </div>
                    }
                    modalCloseTxt="Cancel"
                    modalSaveTxt="Confirm"
                    onClick={handleConfirmReplaceAndHide}
                    showActionBtn={true}
                    fullWidth={false}
                    maxWidth="md"
                />
            )}
        </>
    );
}